import Grid from "@mui/material/Grid";
import React, { useContext } from "react";
import { getAthletes } from "../../store/AtheleteStore/actions";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import { selectCurrentClinician } from "../../store/ClinicianStore/selectors";
import {
  selectAtheletesList,
  selectTotalAtheletes,
  selectLoadingAtheletes,
} from "../../store/AtheleteStore/selectors";
import TableHeader from "./Components/TableHeader";
import { changeSortOfClinicList } from "../../store/ClinicStore/actions";
import {
  selectLoading,
  selectSortConfig,
} from "../../store/ClinicStore/selectors";
import { Button } from "../../components/Buttons";
import { ButtonColorScheme, ButtonType } from "../../types/ui";
import { PAGES } from "../../constants/pages";
import { useHistory } from "react-router-dom";
import TableRow from "./Components/TableRow";
import { Loading } from "../../components/Loading/Loading";
import { StatusENUM } from "../../types/common";
import ConfirmModal from "../../components/ConfirmModal";
import { ClinicRepository } from "../../repositories/ClinicRepository";
import { USER_GROUPS } from "../../constants/roles";
import { AuthContext } from "../../contexts/authContext";

const AtheleteOverview = () => {
  const history = useHistory();
  const coachesList = useAppSelector(selectAtheletesList);
  const dispatch = useAppDispatch();
  const currentClinician = useAppSelector(selectCurrentClinician);
  const isLoading = useAppSelector(selectLoadingAtheletes);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const totalAthletes = useAppSelector(selectTotalAtheletes);
  const [disablingUser, setDisablingUser] = React.useState<{
    userId: string;
    status?: StatusENUM;
  } | null>(null);
  const authContext = useContext(AuthContext);

  const getAthletesList = React.useCallback(
    (replaceItems?: boolean) => {
      dispatch(getAthletes(currentClinician?._id, () => {}, replaceItems));
    },
    [dispatch, disablingUser]
  );

  React.useEffect(() => {
    getAthletesList();
  }, []);
  const clinicRepository = ClinicRepository.getInstance();

  const confirmModalContent = React.useMemo(() => {
    if (disablingUser?.status === StatusENUM.disabled) {
      return "Do you want to reactivate this user?";
    }

    return "Are you sure you want to disable this user?";
  }, [disablingUser]);

  const disableUser = async (userId: string, status: StatusENUM) => {
    setDisablingUser({ userId, status });
    setIsConfirmModalOpen(true);
  };

  const onAcceptConfirmModal = async () => {
    if (disablingUser?.userId) {
      if (disablingUser?.status === StatusENUM.disabled) {
        await clinicRepository.enableClinicianFromCognito(
          disablingUser?.userId
        );
      } else {
        await clinicRepository.disableClinicianFromCognito(
          disablingUser?.userId
        );
      }
      setDisablingUser(null);
      getAthletesList(true);
    }

    setIsConfirmModalOpen(false);
  };

  const resendInvitation = async (email: string) => {
    await authContext.reSendClinicInvite(email, USER_GROUPS.COACH);
    getAthletesList();
  };
  const onConfirmClose = () => {
    setDisablingUser({ userId: "" });
    setIsConfirmModalOpen(false);
  };
  const onClick = (sortBy: string) => {
    dispatch(changeSortOfClinicList(sortBy));
  };
  const sortConfig = useAppSelector(selectSortConfig);

  return (
    <>
      <div className="container max-w-[90%] m-auto pt-[40px]">
        <h1 className="leading-10">
          Hi, Super Admin.
          <br />
          Here is your athlete overview.
        </h1>
        {
          <section className="max-w-[1400px] mt-8 mb-4">
            <Grid item container flexDirection="column">
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Button
                    title={`Invite Coach`}
                    onClick={() => {
                      history.push(PAGES.userConfig);
                    }}
                    type={ButtonType.normal}
                    colorScheme={ButtonColorScheme.gradient}
                    shadowed
                  />
                </Grid>
              </Grid>

              <h3>{`Coaches List`}</h3>
            </Grid>
          </section>
        }
        <TableHeader onClick={onClick} sortConfig={sortConfig} />
        <div className="w-full min-h-[200px] flex flex-col overflow-y-hidden space-y-[5px] relative">
          {coachesList.map((coach: any) => {
            return (
              <TableRow
                authStatus={coach.profileInfo.authStatus}
                key={coach._id}
                name={
                  coach.profileInfo.firstName + " " + coach.profileInfo.lastName
                }
                onDisableUser={
                  coach.profileInfo.authStatus
                    ? () => disableUser(coach._id, coach.status)
                    : undefined
                }
                resendInvitation={() =>
                  resendInvitation(coach.profileInfo.email)
                }
                inviteDates={coach.profileInfo.inviteDates}
                status={coach.profileInfo.authStatus}
                activationStatus={coach.status}
                coachId={coach._id}
              />
            );
          })}
          <Loading loading={isLoading} states={coachesList} />
          <div className="flex justify-end">
            <Button
              classNames="mt-4"
              title="Load More"
              onClick={getAthletesList}
              type={ButtonType.normal}
              disabled={isLoading || totalAthletes === coachesList.length}
              colorScheme={
                totalAthletes !== coachesList.length
                  ? ButtonColorScheme.gradient
                  : ButtonColorScheme.disabled
              }
              shadowed={totalAthletes !== coachesList.length}
            />
          </div>
        </div>
      </div>
      <ConfirmModal
        title={`${
          disablingUser?.status === StatusENUM.disabled
            ? "Reactivate"
            : "Disable"
        } User `}
        open={isConfirmModalOpen}
        onAccept={onAcceptConfirmModal}
        onClose={onConfirmClose}
        content={confirmModalContent}
        acceptButtonText="Yes"
        cancelButtonText="No"
      />
    </>
  );
};

export default AtheleteOverview;
